import { OutOfPocketMaxState, QmeState, ReimbursementRule } from "#/lib/carrot-api/types/Subsidy"
import { Percentages } from "../hooks/useReimbursementRulePercentages"
import { PiiText } from "#/services/tracking"
import { Box, Stack, Typography } from "@carrotfertility/carotene-core"
import React, { FC } from "react"
import { FormattedMessage } from "react-intl"

type YourCostShareQmeOopMaxRulesProps = {
  companyDisplayName: string
  isDefaultCategory: boolean
  getPercentages: (qme: QmeState, oop: OutOfPocketMaxState) => Percentages
  reimbursementRules?: ReimbursementRule[] // unused, kept for compatibility if passed
}

export const YourCostShareQmeOopMaxRules: FC<YourCostShareQmeOopMaxRulesProps> = (props) => {
  return (
    <Stack display="flex" gap={(theme) => theme.tokens.spacing.lg}>
      <QmeOopMaxRuleInfo {...props} />
      <NonQmeOopMaxRuleInfo {...props} />
    </Stack>
  )
}

const CostSharePercent: FC<{ value: number; appendAsterisk?: boolean }> = ({ value, appendAsterisk = false }) => (
  <>
    <FormattedMessage
      defaultMessage="{value, number, ::percent}"
      description="Formatted percentage value in cost share table, e.g. '80%'"
      values={{ value: value / 100 }}
    />
    {appendAsterisk ? "*" : ""}
  </>
)

const QmeOopMaxRuleInfo: FC<YourCostShareQmeOopMaxRulesProps> = (props: YourCostShareQmeOopMaxRulesProps) => {
  const oopMet = props.getPercentages(QmeState.QME, OutOfPocketMaxState.OOP_MAX_MET)
  const oopNotMet = props.getPercentages(QmeState.QME, OutOfPocketMaxState.OOP_MAX_NOT_MET)
  const rowColumns = "minmax(0, 1fr) 72px 72px"

  return (
    <Box
      sx={(theme) => ({
        padding: theme.spacing(theme.tokens.spacing.md),
        display: "flex",
        backgroundColor: theme.palette.background.default,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius
      })}
    >
      <Box width="100%">
        <Box
          sx={(theme) => ({
            paddingBlockStart: theme.spacing(theme.tokens.spacing.xs),
            paddingBlockEnd: theme.spacing(theme.tokens.spacing.sm),
            marginBlockEnd: theme.spacing(theme.tokens.spacing.xs),
            display: "grid",
            gridTemplateColumns: rowColumns,
            columnGap: theme.spacing(theme.tokens.spacing.lg),
            alignItems: "start",
            inlineSize: "100%",
            borderBlockEnd: `1px solid ${theme.palette.divider}`
          })}
        >
          <Typography variant="body1" fontWeight="bold" color={(theme) => theme.palette.text.secondary}>
            <FormattedMessage
              defaultMessage="QMEs"
              description="Table header abbreviation for Qualified Medical Expenses"
            />
          </Typography>
          <Typography
            variant="body1"
            fontWeight="bold"
            color={(theme) => theme.palette.text.secondary}
            sx={{ whiteSpace: "nowrap", textAlign: "end" }}
          >
            <FormattedMessage
              defaultMessage="You pay"
              description="Table column header for the member's cost share percentage"
            />
          </Typography>
          <Typography
            variant="body1"
            fontWeight="bold"
            color={(theme) => theme.palette.text.secondary}
            textAlign="right"
          >
            <FormattedMessage
              defaultMessage="{companyDisplayName} pays"
              description="Table column header for the employer's cost share percentage"
              values={{ companyDisplayName: <PiiText>{props.companyDisplayName}</PiiText> }}
            />
          </Typography>
        </Box>

        <Box
          sx={(theme) => ({
            paddingBlockStart: theme.spacing(theme.tokens.spacing.xs),
            paddingBlockEnd: theme.spacing(theme.tokens.spacing.xs),
            display: "grid",
            gridTemplateColumns: rowColumns,
            columnGap: theme.spacing(theme.tokens.spacing.lg),
            alignItems: "start",
            inlineSize: "100%"
          })}
        >
          <Typography variant="body1" fontWeight="bold" color={(theme) => theme.palette.text.secondary}>
            <FormattedMessage
              defaultMessage="Before you meet your deductible"
              description="Row label in cost share table for the insurance tier before deductible is met"
            />
          </Typography>
          <Typography variant="body1" color={(theme) => theme.palette.text.secondary} textAlign="right">
            <CostSharePercent value={100} />
          </Typography>
          <Typography variant="body1" color={(theme) => theme.palette.text.secondary} textAlign="right">
            <CostSharePercent value={0} />
          </Typography>
        </Box>

        <Box
          sx={(theme) => ({
            paddingBlockStart: theme.spacing(theme.tokens.spacing.xs),
            paddingBlockEnd: theme.spacing(theme.tokens.spacing.xs),
            display: "grid",
            gridTemplateColumns: rowColumns,
            columnGap: theme.spacing(theme.tokens.spacing.lg),
            alignItems: "start",
            inlineSize: "100%"
          })}
        >
          <Stack display="flex" gap={(theme) => theme.tokens.spacing.xs}>
            <Typography
              component="span"
              variant="body1"
              fontWeight="bold"
              color={(theme) => theme.palette.text.secondary}
            >
              <FormattedMessage
                defaultMessage="Once you meet your deductible but before you meet your OOPM"
                description="Row label in cost share table. OOPM = out-of-pocket maximum, an insurance term"
              />
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontStyle: "italic", fontWeight: "normal" }}
              color={(theme) => theme.palette.text.secondary}
            >
              <FormattedMessage
                defaultMessage="*The amount {companyDisplayName} pays does not count toward your OOPM. We do report your own contributions to your insurance so they can count toward your OOPM."
                description="Footnote in cost share table. OOPM = out-of-pocket maximum, an insurance term"
                values={{ companyDisplayName: <PiiText>{props.companyDisplayName}</PiiText> }}
              />
            </Typography>
          </Stack>
          <Typography variant="body1" color={(theme) => theme.palette.text.secondary} textAlign="right">
            <CostSharePercent value={oopNotMet.memberResponsibility} />
          </Typography>
          <Typography variant="body1" color={(theme) => theme.palette.text.secondary} textAlign="right">
            <CostSharePercent value={oopNotMet.companyResponsibility} appendAsterisk />
          </Typography>
        </Box>

        <Box
          sx={(theme) => ({
            paddingBlockStart: theme.spacing(theme.tokens.spacing.xs),
            paddingBlockEnd: theme.spacing(theme.tokens.spacing.xs),
            display: "grid",
            gridTemplateColumns: rowColumns,
            columnGap: theme.spacing(theme.tokens.spacing.lg),
            alignItems: "start",
            inlineSize: "100%"
          })}
        >
          <Typography variant="body1" fontWeight="bold" color={(theme) => theme.palette.text.secondary}>
            <FormattedMessage
              defaultMessage="Once you meet your OOPM"
              description="Row label in cost share table. OOPM = out-of-pocket maximum, an insurance term"
            />
          </Typography>
          <Typography variant="body1" color={(theme) => theme.palette.text.secondary} textAlign="right">
            <CostSharePercent value={oopMet.memberResponsibility} />
          </Typography>
          <Typography variant="body1" color={(theme) => theme.palette.text.secondary} textAlign="right">
            <CostSharePercent value={oopMet.companyResponsibility} />
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

const NonQmeOopMaxRuleInfo: FC<YourCostShareQmeOopMaxRulesProps> = (props) => {
  const oopMet = props.getPercentages(QmeState.NON_QME, OutOfPocketMaxState.OOP_MAX_MET)
  const oopNotMet = props.getPercentages(QmeState.NON_QME, OutOfPocketMaxState.OOP_MAX_NOT_MET)
  const hasOopMaxRule = (oopMet?.companyResponsibility ?? 0) !== (oopNotMet?.companyResponsibility ?? 0)
  if (hasOopMaxRule) {
    throw new Error("Non-QME OOP Max rule is not supported here")
  }
  return (
    <Box
      sx={(theme) => ({
        padding: theme.spacing(theme.tokens.spacing.md),
        display: "flex",
        flexDirection: "column",
        gap: theme.tokens.spacing.xs,
        backgroundColor: theme.palette.background.default,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius
      })}
    >
      <Typography variant="body1" sx={{ fontWeight: "bold" }} color={(theme) => theme.palette.text.secondary}>
        <FormattedMessage
          defaultMessage="Non-QMEs"
          description="Section header abbreviation for Non-Qualified Medical Expenses"
        />
      </Typography>
      <Typography variant="body1" color={(theme) => theme.palette.text.secondary}>
        {props.isDefaultCategory ? (
          <FormattedMessage
            defaultMessage="Your insurance status does not impact your cost share for approved non-QMEs. {companyDisplayName} always pays {companyResponsibilityPercent,number,::percent}."
            description="Explanation that non-qualified medical expenses have a fixed cost share regardless of insurance status"
            values={{
              companyDisplayName: <PiiText>{props.companyDisplayName}</PiiText>,
              companyResponsibilityPercent: oopNotMet.companyResponsibility / 100
            }}
          />
        ) : (
          <FormattedMessage
            defaultMessage="Your insurance status does not impact your cost share for approved non-QMEs in this category. {companyDisplayName} always pays {companyResponsibilityPercent,number,::percent}."
            description="Explanation for a specific benefit category that non-qualified medical expenses have a fixed cost share regardless of insurance status"
            values={{
              companyDisplayName: <PiiText>{props.companyDisplayName}</PiiText>,
              companyResponsibilityPercent: oopNotMet.companyResponsibility / 100
            }}
          />
        )}
      </Typography>
    </Box>
  )
}
